import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { card, chip, download, table } from "../../ui/record";
import { select } from "../clients/forms";

// The caregiver's Earnings (brief 1E-33, mockup v87 earnCard): gross pay by pay period (visits,
// hours, overtime, mileage, gross pay, status), the total, a client or patient filter, and
// Download, which files a statement on the business's letterhead in her Documents. FlowCare stops
// at gross pay. Payroll at View and Sees financials only.

interface Earnings {
  rows: {
    period: string;
    sub: string;
    visits: number;
    hours: string;
    overtime: string;
    mileage: string;
    gross: string;
    status: { text: string; level: string };
  }[];
  total: { visits: number; hours: string; gross: string };
  clients: { id: string; name: string }[];
  patients: { id: string; name: string }[];
  canDownload: boolean;
}

export async function earningsCard(host: HTMLElement, personId: string) {
  const only = { client: "", patient: "" };
  const path = () => {
    const p = new URLSearchParams();
    if (only.client) p.set("client", only.client);
    if (only.patient) p.set("patient", only.patient);
    return `/api/people/${encodeURIComponent(personId)}/earnings${p.size ? `?${p}` : ""}`;
  };
  const first = await api<Earnings>("GET", path());
  if (!first.ok) return host.replaceChildren();
  const client = select(
    "Client",
    [["", "All clients"], ...first.body.clients.map((c) => [c.id, c.name] as const)],
    "",
  );
  const patient = select(
    "Patient",
    [["", "All patients"], ...first.body.patients.map((c) => [c.id, c.name] as const)],
    "",
  );
  const slot = el("div");
  const totalLine = el("div", { class: "tot" });
  const dl = el("button", { type: "button", class: "btn sm" }, icon("download"), " Download");
  dl.addEventListener("click", async () => {
    dl.disabled = true;
    const r = await api<{ file: { id: string; name: string } }>(
      "POST",
      `/api/people/${encodeURIComponent(personId)}/earnings/statement`,
      {
        ...(only.client ? { client: only.client } : {}),
        ...(only.patient ? { patient: only.patient } : {}),
      },
    );
    dl.disabled = false;
    if (!r.ok) return toast("The statement wasn't made", r.body.error);
    toast("Earnings statement downloaded", "Filed in her Documents");
    await download(r.body.file.id, r.body.file.name);
  });
  const draw = (e: Earnings) => {
    slot.replaceChildren(
      e.rows.length
        ? table(
            [
              {
                h: "Pay period",
                f: (r) =>
                  el(
                    "span",
                    {},
                    el("b", {}, r.period),
                    el("br"),
                    el("span", { class: "small muted" }, r.sub),
                  ),
              },
              { h: "Visits", num: true, f: (r) => String(r.visits) },
              { h: "Hours", num: true, f: (r) => r.hours },
              { h: "Overtime", num: true, f: (r) => r.overtime },
              { h: "Mileage", num: true, f: (r) => r.mileage },
              { h: "Gross pay", num: true, f: (r) => r.gross },
              { h: "Status", f: (r) => chip(r.status.text, r.status.level) },
            ],
            e.rows,
            undefined,
            "Earnings",
          )
        : el("p", { class: "muted" }, "No earnings yet."),
    );
    totalLine.replaceChildren(
      el("span", {}, `Shown: ${e.total.visits} visit${e.total.visits === 1 ? "" : "s"}`),
      el("span", {}, `${e.total.hours} hours`),
      el("b", {}, e.total.gross),
    );
  };
  const reload = async () => {
    const r = await api<Earnings>("GET", path());
    if (r.ok) draw(r.body);
  };
  client.input.addEventListener("change", () => {
    only.client = client.input.value;
    void reload();
  });
  patient.input.addEventListener("change", () => {
    only.patient = patient.input.value;
    void reload();
  });
  draw(first.body);
  const c = card(
    el("span", {}, icon("credit-card"), " Earnings"),
    "Gross pay by pay period. Payroll pays it.",
    first.body.canDownload ? [dl] : [],
    el("div", { class: "wvbar", "data-unsaved": "off" }, client.el, patient.el),
    slot,
    totalLine,
  );
  c.setAttribute("aria-label", "Earnings");
  host.replaceChildren(c);
}
